import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { Select } from '../../../shared/components/Select';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useSalesContact, useUpdateSalesContact } from '../hooks/useSalesContacts';
import { ContactActivityPanel } from '../components/ContactActivityPanel';
import { ContactFormModal } from '../components/ContactFormModal';
import { ContactOutreachPanel } from '../../outreach/components/ContactOutreachPanel';
import { ContactReferralPanel } from '../../referrals/components/ContactReferralPanel';
import {
  BUILD_STATUS_LABELS,
  SOURCE_LABELS,
  STAGE_CLASSES,
  STAGE_LABELS,
  type SalesContactBuildStatus,
  type SalesContactStage,
} from '../types';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <dt className="text-sm text-muted-foreground">{label}</dt>
    <dd className="text-sm font-medium text-foreground break-words">{value}</dd>
  </div>
);

const link = (url: string | null) =>
  url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline break-all"
    >
      {url}
    </a>
  ) : (
    '—'
  );

const optionsFrom = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, title]) => ({ value, title }));

/**
 * One contact, its facts, and its activity timeline (PE-2159).
 *
 * Its own route rather than a tab on the list (the `features/merchants/` shape, not
 * `features/sales/`'s single-page tabbed one) — a contact is deep-linked from the timeline,
 * from a search and from a merchant conversation, and a tab cannot be linked to.
 *
 * Stage and build status get inline selects here as well as living in the edit modal: moving
 * a prospect along the pipeline is the single most frequent action on this page, and it
 * auto-appends a STAGE_CHANGE activity to the timeline immediately below.
 */
export const ContactDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.SALES_CONTACT_MANAGE);
  // The outreach kit (PE-2188) and referral attribution (PE-2190) hang off the contact but are
  // separate watchman resources, so each panel is gated on its own grant.
  const canViewOutreach = hasPermission(Permission.SALES_OUTREACH_VIEW);
  const canManageOutreach = hasPermission(Permission.SALES_OUTREACH_MANAGE);
  const canManageReferrals = hasPermission(Permission.REFERRAL_MANAGE);

  const { data: contact, isLoading, error } = useSalesContact(id);
  const updateContact = useUpdateSalesContact();
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return <div className="p-4 sm:p-6 text-sm text-muted-foreground">Loading contact…</div>;
  }

  if (error || !contact) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <p className="text-sm text-red-500">Could not load this contact.</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/contacts')}>
          Back to contacts
        </Button>
      </div>
    );
  }

  const setStage = (stage: SalesContactStage) =>
    updateContact.mutate({ id: contact.id, payload: { stage } });

  const setBuildStatus = (buildStatus: SalesContactBuildStatus) =>
    updateContact.mutate({ id: contact.id, payload: { buildStatus } });

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {/* inline-flex + min-h-10 on the inline text buttons here and below: a bare text
              button measures ~20px tall, well under the 40px tap target the responsive audit
              enforces below 1024. Type size and the underline-on-hover are unchanged. */}
          <button
            onClick={() => navigate('/contacts')}
            className="text-sm text-blue-600 hover:underline inline-flex items-center min-h-10"
          >
            ← Contacts
          </button>
          <h1 className="text-2xl font-bold text-foreground mt-1">{contact.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_CLASSES[contact.stage]}`}
            >
              {STAGE_LABELS[contact.stage]}
            </span>
            <span className="text-sm text-muted-foreground">
              {contact.businessName ?? 'No business name'} · {SOURCE_LABELS[contact.source]}
            </span>
          </div>
        </div>
        {canManage && <Button onClick={() => setEditing(true)}>Edit</Button>}
      </div>

      {canManage && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-base font-semibold text-foreground mb-1">Pipeline</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Changing the stage records a stage-change entry in the activity below.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[560px]">
            <Select
              label="Stage"
              options={optionsFrom(STAGE_LABELS)}
              value={contact.stage}
              onChange={(value) => setStage(value as SalesContactStage)}
              disabled={updateContact.isPending}
            />
            <Select
              label="Build status"
              options={optionsFrom(BUILD_STATUS_LABELS)}
              value={contact.buildStatus}
              onChange={(value) => setBuildStatus(value as SalesContactBuildStatus)}
              disabled={updateContact.isPending}
            />
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-base font-semibold text-foreground mb-4">Details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          <Fact label="Role" value={contact.role ?? '—'} />
          <Fact
            label="Email"
            value={
              contact.email ? (
                <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                  {contact.email}
                </a>
              ) : (
                '—'
              )
            }
          />
          <Fact label="Phone" value={contact.phone ?? '—'} />
          <Fact label="Instagram" value={contact.igHandle ?? '—'} />
          <Fact
            label="Instagram followers"
            value={contact.igFollowerCount != null ? contact.igFollowerCount.toLocaleString() : '—'}
          />
          <Fact
            label="Other handles"
            value={contact.otherHandles.length ? contact.otherHandles.join(', ') : '—'}
          />
          <Fact label="Website" value={link(contact.website)} />
          <Fact label="Solution interest" value={contact.solutionInterest ?? '—'} />
          <Fact label="Category" value={contact.category ?? '—'} />
          <Fact label="Primary audience" value={contact.primaryAudience ?? '—'} />
          <Fact label="Visual quality" value={contact.visualQualityScore ?? '—'} />
          <Fact label="Growth mindset" value={contact.growthMindsetScore ?? '—'} />
          <Fact
            label="Celebrity / press attention"
            value={contact.hasCelebrityOrPressAttention ? 'Yes' : 'No'}
          />
          <Fact label="Assets folder" value={link(contact.assetsFolderUrl)} />
          <Fact label="Design link" value={link(contact.designLinkUrl)} />
          <Fact label="Assigned rep" value={contact.assignedRepId ?? '—'} />
          <Fact
            label="Linked merchant"
            value={
              contact.merchantId ? (
                <button
                  onClick={() => navigate(`/merchants/${contact.merchantId}`)}
                  className="text-blue-600 hover:underline inline-flex items-center min-h-10"
                >
                  Open merchant
                </button>
              ) : (
                '—'
              )
            }
          />
          <Fact label="Build status" value={BUILD_STATUS_LABELS[contact.buildStatus]} />
          <Fact label="Created" value={formatDate(contact.createdAt)} />
          <Fact label="Last updated" value={formatDate(contact.updatedAt)} />
        </dl>
      </div>

      {canViewOutreach && (
        <ContactOutreachPanel contactId={contact.id} canManage={canManageOutreach} />
      )}

      {canManageReferrals && <ContactReferralPanel contactId={contact.id} />}

      <ContactActivityPanel contactId={contact.id} />

      {editing && <ContactFormModal contact={contact} onClose={() => setEditing(false)} />}
    </div>
  );
};
